import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { formatMeasurementPeriodCompactRangeLabel } from '@act/shared/data-transfer-objects';
import SectionReveal from "./SectionReveal";
import SectionBanner from "./SectionBanner";
import { HostedReportSectionLayout } from '../HostedReportSectionLayout';
import type { ReportHostedSectionProps } from '../reportHostedSection.types';
import { foreground, muted, border, secondary, display, body, CORAL, NAVY } from "./tokens";

const DEFAULT_ENGAGEMENT_DATA = [
  { month: "November 2025", engaged: 725, interacted: 1435 },
  { month: "December 2025", engaged: 754, interacted: 1469 },
  { month: "January 2026", engaged: 814, interacted: 1581 },
  { month: "February 2026", engaged: 986, interacted: 1930 },
  { month: "March 2026", engaged: 1114, interacted: 2170 },
  { month: "April 2026", engaged: 1124, interacted: 2187 },
];

const DEFAULT_FUNNEL_ROWS = [
  { label: "Ever Eligible", value: 10650, width: 100, note: "Full eligible population", isHighlight: false },
  { label: "Ever Interacted", value: 2440, width: 22.9, note: "Members who responded to outreach", isHighlight: false },
  { label: "Ever Engaged", value: 1270, width: 11.9, note: "Members in an active guide relationship", isHighlight: true },
];

type FunnelRow = {
  label: string;
  value: number;
  width: number;
  note: string;
  isHighlight: boolean;
};

type DenominatorCard = {
  label: string;
  value: string;
  hint: string;
  variant: string;
};

const buildFunnelRows = (data: Record<string, unknown>): FunnelRow[] => {
  const funnel = Array.isArray(data.funnel)
    ? (data.funnel as Array<{ label: string; value: number; subCopy?: string }>)
    : [];
  if (!funnel.length) {
    return DEFAULT_FUNNEL_ROWS;
  }
  const max = funnel[0]?.value ?? 1;
  return funnel.map((step, index) => ({
    label: step.label,
    value: step.value,
    width: max > 0 ? (step.value / max) * 100 : 0,
    note: step.subCopy ?? '',
    isHighlight: index === funnel.length - 1,
  }));
};

const buildDenominators = (data: Record<string, unknown>): DenominatorCard[] => {
  if (!Array.isArray(data.denominators)) {
    return [];
  }
  return data.denominators as DenominatorCard[];
};

/**
 * Member engagement section with trend chart, funnel, and denominator cards.
 * @param props - Resolved engagement-story snapshot.
 */
const EngagementStory = ({
  data,
  reportingPeriod,
  measurementPeriod,
}: ReportHostedSectionProps) => {
  const funnelPeriodLabel = formatMeasurementPeriodCompactRangeLabel(
    measurementPeriod ?? reportingPeriod,
  );
  const engagementData = Array.isArray(data.trend)
    ? (data.trend as Array<{ month: string; engaged: number; interacted: number }>)
    : DEFAULT_ENGAGEMENT_DATA;
  const funnelRows = buildFunnelRows(data);
  const denominators = buildDenominators(data);

  return (
  <HostedReportSectionLayout
    sectionDomId="engagement"
    className="section--white report-hosted-section-body"
    style={{ background: "#fff", fontFamily: body }}
    banner={
      <SectionBanner title="Member Engagement" iconHref="#icon_relationship_engine" />
    }
  >
      <SectionReveal delay={0.15}>
        <div className="mb-16">
          <div className="flex justify-end mb-6">
            <div className="flex gap-4 text-xs" style={{ color: muted }}>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full" style={{ background: CORAL }} />
                Engaged
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full" style={{ background: NAVY }} />
                Interacted
              </span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={engagementData}>
              <defs>
                <linearGradient id="engagedGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CORAL} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={CORAL} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="interactedGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={NAVY} stopOpacity={0.15} />
                  <stop offset="100%" stopColor={NAVY} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E0D6" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#6B7A8D" angle={-30} textAnchor="end" height={70} interval={0} />
              <YAxis tick={{ fontSize: 12 }} stroke="#6B7A8D" />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8E0D6", fontSize: 13 }} />
              <Area type="monotone" dataKey="interacted" stroke={NAVY} fill="url(#interactedGrad)" strokeWidth={2} />
              <Area type="monotone" dataKey="engaged" stroke={CORAL} fill="url(#engagedGrad)" strokeWidth={2.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </SectionReveal>

      <SectionReveal delay={0.1}>
        <div style={{ marginBottom: "2.5rem" }}>
          <h3
            style={{
              fontFamily: display,
              fontWeight: 600,
              fontSize: "var(--reema-text-h2)",
              color: foreground,
              margin: "0 0 0.75rem",
              lineHeight: 1.2,
              letterSpacing: "-0.01em",
            }}
          >
            Engagement rate depends on the denominator.
          </h3>
          <p
            style={{
              fontFamily: body,
              fontSize: "var(--reema-text-body-lg)",
              color: muted,
              margin: 0,
              lineHeight: 1.6,
            }}
          >
            Here{"\u2019"}s the full picture {"\u2014"} from the broadest eligible pool down to the members who actively chose to work with a guide this month.
          </p>
        </div>


        <div className="engagement-funnel-grid grid md:grid-cols-2 gap-8 items-stretch">
          {/* Stepped funnel */}
          <div className="rounded-2xl p-6 shadow-sm" style={{ background: "#fff", border: `1px solid ${border}` }}>
            <h4 className="text-sm font-semibold mb-5 uppercase tracking-wider" style={{ color: foreground }}>Overall Engagement Funnel · {funnelPeriodLabel}</h4>
            <div className="space-y-3">
              {funnelRows.map((row) => (
                <div key={row.label}>
                  <div className="flex items-baseline justify-between text-sm mb-1">
                    <span className="font-medium" style={{ color: foreground }}>{row.label}</span>
                    <span className="tabular-nums font-semibold" style={{ color: foreground }}>{row.value.toLocaleString()}</span>
                  </div>
                  <div className="h-7 rounded-md overflow-hidden" style={{ background: secondary }}>
                    <div
                      className="h-full rounded-md transition-all"
                      style={{
                        width: `${Math.max(row.width, 4)}%`,
                        background: row.isHighlight ? CORAL : NAVY,
                        opacity: row.isHighlight ? 1 : 0.85,
                      }}
                    />
                  </div>
                  <p className="text-xs mt-1.5" style={{ color: muted }}>{row.note}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Three rate cards */}
          <div className="flex flex-col gap-3">
            {denominators.length > 0 ? (
              denominators.map(card => (
                <div
                  key={card.label}
                  className="rounded-2xl p-5"
                  style={{ background: '#fff', border: `1px solid ${border}` }}
                >
                  <div className="flex items-baseline justify-between mb-1">
                    <span
                      className="text-3xl font-semibold tabular-nums"
                      style={{ color: muted }}
                    >
                      {card.value}
                    </span>
                    <span
                      className="text-[11px] uppercase tracking-wider"
                      style={{ color: muted }}
                    >
                      {card.label}
                    </span>
                  </div>
                  <p className="text-xs mt-1" style={{ color: muted }}>
                    {card.hint}
                  </p>
                </div>
              ))
            ) : (
              <>
                <div className="rounded-2xl p-5" style={{ background: '#fff', border: `1px solid ${border}` }}>
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-3xl font-semibold tabular-nums" style={{ color: muted }}>12%</span>
                    <span className="text-[11px] uppercase tracking-wider" style={{ color: muted }}>Engagement rate</span>
                  </div>
                  <p className="text-sm font-medium" style={{ color: foreground }}>Engaged ÷ ever eligible</p>
                </div>
                <div className="rounded-2xl p-5" style={{ background: '#fff', border: `1px solid ${border}` }}>
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-3xl font-semibold tabular-nums" style={{ color: muted }}>52%</span>
                    <span className="text-[11px] uppercase tracking-wider" style={{ color: muted }}>Conversion rate</span>
                  </div>
                  <p className="text-sm font-medium" style={{ color: foreground }}>Engaged ÷ ever interacted</p>
                </div>
              </>
            )}
          </div>
        </div>
      </SectionReveal>
  </HostedReportSectionLayout>
  );
};

export default EngagementStory;
